/**
 * useDecisionLog — pure transform hook (RAISE-16886).
 *
 * Accepts GovernanceArtifactItem[], filters to ADRs, extracts structured
 * metadata, and provides sort controls. No API calls.
 */

import { useState, useMemo } from "react";
import type { GovernanceArtifactItem } from "@/hooks/useGovernanceArtifacts";
import {
  extractAdrStatus,
  extractSupersededBy,
  type AdrStatus,
} from "@/lib/governancePulse";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SortKey = "date" | "adrNumber" | "status" | "title";
export type SortDir = "asc" | "desc";

export interface DecisionLogEntry {
  artifact: GovernanceArtifactItem;
  adrNumber: number;
  /** Display title derived from filename. */
  title: string;
  status: AdrStatus;
  supersededBy: string | null;
  /** artifact.synced_at ISO string */
  date: string;
}

export interface DecisionLogResult {
  entries: DecisionLogEntry[];
  sortKey: SortKey;
  sortDir: SortDir;
  setSort: (key: SortKey, dir: SortDir) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const ADR_NUMBER_PATTERN = /adr-(\d+)/i;

function extractAdrNumber(path: string): number {
  const filename = path.split("/").pop() ?? path;
  const match = ADR_NUMBER_PATTERN.exec(filename);
  return match ? parseInt(match[1], 10) : 0;
}

function extractTitle(path: string): string {
  const filename = path.split("/").pop() ?? path;
  // Remove extension
  const withoutExt = filename.replace(/\.[^.]+$/, "");
  // Remove "adr-NNN-" prefix
  const withoutPrefix = withoutExt.replace(/^adr-\d+-/i, "");
  // Convert kebab-case to Title Case
  return withoutPrefix
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function compareEntries(a: DecisionLogEntry, b: DecisionLogEntry, key: SortKey, dir: SortDir): number {
  let cmp = 0;
  switch (key) {
    case "date":
      cmp = a.date.localeCompare(b.date);
      break;
    case "adrNumber":
      cmp = a.adrNumber - b.adrNumber;
      break;
    case "status":
      cmp = a.status.localeCompare(b.status);
      break;
    case "title":
      cmp = a.title.localeCompare(b.title);
      break;
  }
  return dir === "asc" ? cmp : -cmp;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useDecisionLog(
  artifacts: GovernanceArtifactItem[],
): DecisionLogResult {
  const [sortKey, setSortKey] = useState<SortKey>("adrNumber");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  const setSort = (key: SortKey, dir: SortDir) => {
    setSortKey(key);
    setSortDir(dir);
  };

  const entries = useMemo<DecisionLogEntry[]>(() => {
    const adrs = artifacts.filter((a) => a.type === "adr");
    const mapped = adrs.map((a): DecisionLogEntry => ({
      artifact: a,
      adrNumber: extractAdrNumber(a.path),
      title: extractTitle(a.path),
      status: extractAdrStatus(a.content_summary),
      supersededBy: extractSupersededBy(a.content_summary),
      date: a.synced_at,
    }));
    return [...mapped].sort((a, b) => compareEntries(a, b, sortKey, sortDir));
  }, [artifacts, sortKey, sortDir]);

  return { entries, sortKey, sortDir, setSort };
}
